import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Layers, Play, Check, ScrollText, Map as MapIcon, Scale, TrendingUp, Leaf,
  FlaskConical, Palette, Globe, Users, Compass, Landmark, LifeBuoy,
} from 'lucide-react';
import { downloadPrelimsPdf, studyAPI } from '../../services/api';
import { getRememberDone, replaceRememberDone } from './rememberProgress';
import templeImg from '../../assets/temple-far1.jpg';
import logoImg from '../../assets/logo.png';
import './SubjectPicker.css';

const getTodayDate = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
}).format(new Date());

const SUBJECTS = [
  { name: 'History',                 gs: 'GS-I',   Icon: ScrollText },
  { name: 'Geography',               gs: 'GS-I',   Icon: MapIcon },
  { name: 'Polity',                  gs: 'GS-II',  Icon: Scale },
  { name: 'Economy',                 gs: 'GS-III', Icon: TrendingUp },
  { name: 'Environment',             gs: 'GS-III', Icon: Leaf },
  { name: 'Disaster Management',     gs: 'GS-III', Icon: LifeBuoy },
  { name: 'Science & Technology',    gs: 'GS-III', Icon: FlaskConical },

  { name: 'Art & Culture',           gs: 'GS-I',   Icon: Palette },
  { name: 'International Relations', gs: 'GS-II',  Icon: Globe },
  { name: 'Social Issues',           gs: 'GS-II',  Icon: Users },
  { name: 'Ethics',                  gs: 'GS-IV',  Icon: Compass },
  { name: 'Governance',              gs: 'GS-II',  Icon: Landmark },
];

/* ══════════════════════════════════════════════════════════════
   Remember road: one winding road, one stop per subject.
   Pure geometry first, so it can be checked without a browser.
   ══════════════════════════════════════════════════════════════ */
const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const r1 = n => Math.round(n * 10) / 10;
const pt = p => `${r1(p.x)} ${r1(p.y)}`;

/* Smooth cubic segments through the points (Catmull-Rom) */
function curveThrough(points) {
  const segs = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    segs.push([
      p1,
      { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 },
      { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 },
      p2,
    ]);
  }
  return segs;
}

function halves([p0, c1, c2, p3]) {
  const q0 = lerp(p0, c1, 0.5);
  const q1 = lerp(c1, c2, 0.5);
  const q2 = lerp(c2, p3, 0.5);
  const a = lerp(q0, q1, 0.5);
  const b = lerp(q1, q2, 0.5);
  const s = lerp(a, b, 0.5);
  return [[p0, q0, a, s], [s, b, q2, p3]];
}

const pathOf = segs => `M${pt(segs[0][0])} ${segs.map(([, c1, c2, p3]) => `C${pt(c1)} ${pt(c2)} ${pt(p3)}`).join(' ')}`;

export function buildRoad(vertical) {
  const N = SUBJECTS.length;
  let W; let H; let T; let at; let place;

  if (!vertical) {
    // wide: the road runs left to right, labels sit above and below it
    W = 1200; H = 680; T = 64;
    at = j => ({ x: 60 + (980 * j) / (N - 1), y: 340 + 95 * Math.cos((3 * Math.PI * j) / (N - 1)) });
    place = (j, p) => {
      const below = j % 2 === 0;
      return {
        side: below ? 'down' : 'up',
        bx: p.x - 6, by: below ? 508 : 30, bw: 156,
        // dashed connector from the road edge to the label
        line: below ? { x1: p.x, y1: p.y + T / 2, x2: p.x, y2: 500 } : { x1: p.x, y1: p.y - T / 2, x2: p.x, y2: 168 },
        dot: below ? { x: p.x, y: 500 } : { x: p.x, y: 168 },
      };
    };
  } else {
    // narrow: the road runs top to bottom, labels sit left and right of it
    W = 400; H = 1860; T = 40;
    at = j => ({ x: 200 + 50 * Math.cos((3 * Math.PI * j) / (N - 1)), y: 150 + (1550 * j) / (N - 1) });
    place = (j, p) => {
      const right = j % 2 === 1;
      return {
        side: right ? 'right' : 'left',
        bx: right ? 284 : 8, by: p.y - 38, bw: 108,
        line: right ? { x1: p.x + T / 2, y1: p.y, x2: 278, y2: p.y } : { x1: p.x - T / 2, y1: p.y, x2: 122, y2: p.y },
        dot: right ? { x: 278, y: p.y } : { x: 122, y: p.y },
      };
    };
  }

  const stopsAt = Array.from({ length: N }, (_, j) => at(j));
  const points = [at(-1.5), ...stopsAt, at(N + 0.5)];
  const segs = curveThrough(points);

  // each stop owns the stretch of road around it (second half of the way in, first half of the way out)
  const owned = stopsAt.map((_, j) => {
    const into = halves(segs[j])[1];
    const out = halves(segs[j + 1])[0];
    return pathOf([into, out]);
  });

  return {
    W, H, T, full: pathOf(segs), owned,
    stops: stopsAt.map((p, j) => ({ x: p.x, y: p.y, ...place(j, p) })),
  };
}

function RememberRoad({ road, done, nextIdx, pathFor }) {
  const { W, H, T, full, owned, stops } = road;
  const pct = (v, total) => `${(v / total) * 100}%`;
  const nextStop = nextIdx >= 0 ? stops[nextIdx] : null;

  return (
    <div className="rd-wrap">
      <div className="rd-ghost" style={{ backgroundImage: `url(${templeImg})` }} aria-hidden="true" />

      <div className="rd" style={{ aspectRatio: `${W} / ${H}`, '--tw': W }}>
        <svg className="rd-svg" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
          <defs>
            <pattern id="rd-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
              <line x1="0" y1="0" x2="0" y2="7" stroke="rgba(255,255,255,.2)" strokeWidth="2" />
            </pattern>
            <filter id="rd-rough" x="-5%" y="-5%" width="110%" height="110%">
              <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="4" result="n" />
              <feDisplacementMap in="SourceGraphic" in2="n" scale="5" />
            </filter>
          </defs>

          <g className="rd-road" filter="url(#rd-rough)">
            <path d={full} className="rd-base" strokeWidth={T} />
            {owned.map((d, j) => done.has(SUBJECTS[j].name) && <path key={j} d={d} className="rd-glad" strokeWidth={T} />)}
            <path d={full} stroke="url(#rd-hatch)" strokeWidth={T} fill="none" />
          </g>
          <path d={full} className="rd-dash" strokeWidth="3" />

          {stops.map((s, j) => (
            <g key={j} className={`rd-link ${done.has(SUBJECTS[j].name) ? 'is-done' : ''}`}>
              <line x1={s.line.x1} y1={s.line.y1} x2={s.line.x2} y2={s.line.y2} />
              <circle cx={s.dot.x} cy={s.dot.y} r="5" />
            </g>
          ))}
        </svg>

        {stops.map((s, j) => {
          const subj = SUBJECTS[j];
          const finished = done.has(subj.name);
          const cls = ['rd-stop', `rd-stop--${s.side}`, finished ? 'is-done' : '', j === nextIdx ? 'is-next' : ''].join(' ');
          return (
            <Link key={subj.name} to={pathFor(subj.name)} className={cls}
              aria-label={`${subj.name}${finished ? ', finished' : ''}`}
              style={{ left: pct(s.bx, W), top: pct(s.by, H), width: pct(s.bw, W), '--i': j }}>
              <span className="rd-num">
                {String(j + 1).padStart(2, '0')}.
                <span className="rd-ico"><subj.Icon strokeWidth={1.5} /></span>
                {finished && <span className="rd-check"><Check strokeWidth={3.2} /></span>}
              </span>
              <span className="rd-name">{subj.name}</span>
            </Link>
          );
        })}

        {nextStop && (
          <Link to={pathFor(SUBJECTS[nextIdx].name)} className="rd-marker"
            aria-label={`Continue ${SUBJECTS[nextIdx].name} Remember questions`}
            title={`Continue ${SUBJECTS[nextIdx].name} Remember questions`}
            style={{ left: pct(nextStop.x, W), top: pct(nextStop.y, H) }}>
            <img src={logoImg} alt="" />
          </Link>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const NARROW = '(max-width: 760px)';

export default function SubjectPicker({ examType }) {
  const [questionDays, setQuestionDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(getTodayDate);
  const [section, setSection] = useState('bank');
  const [mode, setMode] = useState(() => (examType === 'Prelims' ? 'remember' : 'practice'));
  const [loading, setLoading] = useState(true);
  const [daysError, setDaysError] = useState('');
  const [pdfLoading, setPdfLoading] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfOpen, setPdfOpen] = useState(false);
  const [vertical, setVertical] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);
  const [tick, setTick] = useState(0);
  const toolsRef = useRef(null);

  const syncRememberProgress = useCallback(async () => {
    try {
      const { data } = await studyAPI.getRememberProgress({ examType });
      const serverDone = new Set(data.subjects || []);
      const localDone = getRememberDone(examType);
      const localOnly = [...localDone].filter(subject => !serverDone.has(subject));
      if (localOnly.length) {
        await Promise.all(localOnly.map(subject => studyAPI.markRememberDone({ examType, subject })));
      }
      const latestLocal = getRememberDone(examType);
      replaceRememberDone(examType, [...new Set([...serverDone, ...latestLocal])]);
    } catch (error) {
      console.error('Could not sync Remember progress:', error);
    }
  }, [examType]);

  useEffect(() => {
    studyAPI.getQuestionDays({ examType })
      .then(res => {
        const days = Array.isArray(res.data?.days) ? res.data.days : [];
        setQuestionDays(days);
        if (days.length) {
          const latestDay = days.reduce((latest, day) => (
            day.date > latest.date ? day : latest
          ));
          setSelectedDay(latestDay.date);
        }
      })
      .catch(error => {
        console.error('Could not load question days:', error);
        setDaysError(error.response?.data?.message || 'Could not load available practice dates.');
      })
      .finally(() => setLoading(false));
  }, [examType]);

  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setVertical(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  /* Coming back from a subject: re-read which stops are finished */
  useEffect(() => {
    if (examType !== 'Prelims') return undefined;
    const bump = () => setTick(t => t + 1);
    const refresh = () => {
      bump();
      syncRememberProgress();
    };
    window.addEventListener('storage', bump);
    window.addEventListener('focus', refresh);
    window.addEventListener('cf-remember-progress', bump);
    syncRememberProgress();
    return () => {
      window.removeEventListener('storage', bump);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('cf-remember-progress', bump);
    };
  }, [examType, syncRememberProgress]);

  /* PDF popover closes on outside click or Esc */
  useEffect(() => {
    if (!pdfOpen) return undefined;
    const onDown = e => { if (toolsRef.current && !toolsRef.current.contains(e.target)) setPdfOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setPdfOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [pdfOpen]);

  const isPrelims = examType === 'Prelims';
  const practice = isPrelims ? mode === 'practice' : true;
  const todayDate = getTodayDate();
  const activeDay = section === 'today' ? todayDate : selectedDay;
  const latestDate = questionDays.length
    ? questionDays.reduce((latest, day) => (day.date > latest.date ? day : latest)).date
    : null;

  /* Prelims practice is a list of papers, one row per question-day: Today shows just today's row, Bank shows all */
  const listDays = section === 'today' ? questionDays.filter(day => day.date === todayDate) : questionDays;

  const chooseDay = day => {
    setSelectedDay(day);
    setSection('bank');
  };

  /* Prelims Remember uses the due queue; daily practice, including Mains, is tied to a question-day. */
  const subjectPath = subject => {
    const path = `/${isPrelims ? 'prelims' : 'mains'}/${encodeURIComponent(subject)}`;
    const params = new URLSearchParams({ mode });
    if (practice) params.set('date', activeDay);
    return `${path}?${params.toString()}`;
  };

  const formatShort = date => {
    const d = new Date(`${date}T12:00:00+05:30`);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: '2-digit' }),
    });
  };

  const downloadPaper = async (date, answerSheet) => {
    const key = `${date}-${answerSheet ? 'answers' : 'questions'}`;
    setPdfLoading(key);
    setPdfError('');
    try {
      await downloadPrelimsPdf({ date, answerSheet });
    } catch (error) {
      setPdfError(error.response?.data?.message || error.message || 'Could not create the PDF.');
    } finally {
      setPdfLoading('');
    }
  };

  const pdfRows = [
    { label: formatShort(activeDay), date: activeDay },
    { label: 'All', date: 'all' },
  ];

  /* Remember: today's finished subjects, and where to go next */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const done = useMemo(() => getRememberDone(examType), [examType, tick, mode]);
  const nextIdx = SUBJECTS.findIndex(s => !done.has(s.name));
  const road = useMemo(() => buildRoad(vertical), [vertical]);

  return (
    <div className="sp">
      <h1 className="sp-sr">{examType}</h1>

      {/* Top band over a slice of the temple: everything you can set lives here */}
      <header className="sp-band" style={{ backgroundImage: `url(${templeImg})` }}>
        <span className="sp-exam">{examType}</span>

        <div className="sp-center">
          <div className="sp-seg" role="tablist" aria-label="Questions">
            <button type="button" role="tab" disabled={!practice} aria-selected={practice && section === 'today'}
              className={practice && section === 'today' ? 'on' : ''}
              onClick={() => setSection('today')}>Today</button>
            <button type="button" role="tab" disabled={!practice} aria-selected={practice && section === 'bank'}
              className={practice && section === 'bank' ? 'on' : ''}
              onClick={() => { if (latestDate) chooseDay(latestDate); else setSection('bank'); }}>Bank</button>
          </div>
          {isPrelims && (
            <>
              <div className="sp-seg" role="group" aria-label="Mode">
                <button type="button" aria-pressed={practice} className={practice ? 'on' : ''}
                  onClick={() => setMode('practice')}>Practice</button>
                <button type="button" aria-pressed={!practice} className={!practice ? 'on' : ''}
                  onClick={() => setMode('remember')}>Remember</button>
              </div>

              {!practice && (nextIdx === -1 ? (
                <span className="sp-start is-done" role="status"><Check size={16} strokeWidth={3} />Done</span>
              ) : (
                <Link to={subjectPath(SUBJECTS[nextIdx].name)} className="sp-start">
                  <Play size={15} fill="currentColor" />{done.size === 0 ? 'Start' : 'Continue'}
                </Link>
              ))}
            </>
          )}
        </div>

        <div className="sp-tools" ref={toolsRef}>
          {isPrelims && (
            <>
              <button type="button" className={`sp-iconbtn ${pdfOpen ? 'on' : ''}`} aria-label="Download PDFs" title="PDF"
                aria-haspopup="true" aria-expanded={pdfOpen} onClick={() => setPdfOpen(o => !o)}>
                <Download size={18} />
              </button>
              {pdfOpen && (
                <div className="sp-pop">
                  {pdfRows.map(row => (
                    <div key={row.date} className="sp-pop-row">
                      <span>{row.label}</span>
                      {[false, true].map(answers => {
                        const key = `${row.date}-${answers ? 'answers' : 'questions'}`;
                        return (
                          <button key={key} type="button" disabled={!!pdfLoading} onClick={() => downloadPaper(row.date, answers)}>
                            {pdfLoading === key ? '…' : (answers ? 'Answers' : 'Questions')}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                  {pdfError && <p className="sp-pop-error" role="alert">{pdfError}</p>}
                </div>
              )}
            </>
          )}
          <Link to="/browse" className="sp-iconbtn" aria-label="Browse all" title="Browse"><Layers size={18} /></Link>
        </div>
      </header>

      {!practice ? (
        <RememberRoad road={road} done={done} nextIdx={nextIdx} pathFor={subjectPath} />
      ) : (
        <section className="sp-practice" aria-labelledby="sp-practice-title">
          <h2 id="sp-practice-title">
            {isPrelims
              ? (section === 'today' ? "Today's practice paper" : 'Daily practice papers')
              : (section === 'today' ? "Today's Mains practice" : 'Daily Mains practice')}
          </h2>
          {loading ? (
            <p className="sp-practice-state" role="status">Loading practice dates…</p>
          ) : daysError ? (
            <p className="sp-practice-error" role="alert">{daysError}</p>
          ) : listDays.length ? (
            <div className="sp-practice-scroll">
              <table className="sp-practice-table">
                <thead>
                  <tr>
                    <th scope="col">Test name</th>
                    <th scope="col"># Questions</th>
                    {isPrelims
                      ? <th scope="col">Max marks</th>
                      : <th scope="col">Subjects</th>}
                    <th scope="col"><span className="sp-practice-action-heading">Practice</span></th>
                  </tr>
                </thead>
                <tbody>
                  {listDays.map(day => {
                    const count = (day.subjects || []).reduce((sum, item) => sum + item.count, 0);
                    const destination = isPrelims
                      ? `/prelims/practice/${encodeURIComponent(day.date)}`
                      : `/mains/practice/${encodeURIComponent(day.date)}`;
                    return (
                      <tr key={day.date}>
                        <th scope="row">{formatShort(day.date)}</th>
                        <td>{count}</td>
                        {isPrelims
                          ? <td>{count * 2}</td>
                          : <td>{(day.subjects || []).map(item => item.subject).join(', ')}</td>}
                        <td>
                          <Link
                            className="sp-practice-button"
                            to={destination}
                            onClick={() => setSelectedDay(day.date)}
                          >
                            Practice Now
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="sp-practice-state">
              {section === 'today'
                ? 'No practice paper for today yet.'
                : 'No practice papers are available yet.'}
            </p>
          )}
        </section>
      )}
    </div>
  );
}